import { useState, useEffect } from 'react';
import { CheckCircle, TrendingUp, AlertCircle, BarChart3, Target, FileText, Eye, Download, X } from 'lucide-react';
import { RadarChart } from '../RadarChart';
import { projectGovernanceService } from '../../services/projectGovernance';
import { projectScopeService, type ProjectChangeRequest } from '../../services/projectScope';
import { toast } from 'sonner';
import type { ApiErrorLike } from '../../utils/apiError'

// Sprint 20 — DEMO change requests render only in the !projectId
// preview / marketing branch. Real projects load from the backend
// (POST /projects/{id}/scope/change-requests shipped in Part 9).
const CR_CATEGORIES = ['Scope', 'Time', 'Cost', 'Quality', 'Resource', 'Other'];

// Demo data — rendered ONLY when projectId is unset (preview / marketing mode).
// Real projects must never see these fake percentages; falling through to mock
// during an outage or on a fresh project violates feedback_silent_fallback.
type GovMetric = { label: string; value: string; trend: string; status: string };
type GovDimension = { dimension: string; score: number; maxScore: number };
type GovCompliance = { category: string; score: number; target: number; gap: number; status: string };
type GovImprovement = { area: string; priority: string; currentScore: number; targetScore: number; gaps: string[]; actions: string[] };
type GovTrend = { month: string; score: number };

interface GovernanceSectionProps {
  projectId?: number;
}

export function GovernanceSection({ projectId }: GovernanceSectionProps) {
  const [activeSubTab, setActiveSubTab] = useState<'overview' | 'compliance' | 'improvements'>('overview');
  // Real projects start empty; demo data is reserved for the !projectId branch.
  const [governanceMetrics, setGovernanceMetrics] = useState<GovMetric[]>(
    [],
  );
  const [governanceDimensions, setGovernanceDimensions] = useState<GovDimension[]>(
    [],
  );
  const [complianceByCategory, setComplianceByCategory] = useState<GovCompliance[]>(
    [],
  );
  const [improvementAreas, setImprovementAreas] = useState<GovImprovement[]>(
    [],
  );
  const [complianceTrend, setComplianceTrend] = useState<GovTrend[]>(
    [],
  );

  // Sprint 20 — real change-request load + create flow. Replaces the
  // hardcoded mock list ("Sarah Wilson", "Robert Lee", CR-2024-089/088)
  // that rendered identically for every project.
  const [changeRequests, setChangeRequests] = useState<ProjectChangeRequest[]>(
    [],
  );
  const [showChangeRequestForm, setShowChangeRequestForm] = useState(false);
  const [savingChangeRequest, setSavingChangeRequest] = useState(false);

  const loadChangeRequests = () => {
    if (!projectId) {
      setChangeRequests([]);
      return;
    }
    projectScopeService
      .listChangeRequests(projectId)
      .then(setChangeRequests)
      .catch(() => {
        toast.error('Failed to load change requests');
        // No silent fallback for real projects — see RisksSection.
        setChangeRequests([]);
      });
  };

  useEffect(() => {
    if (!projectId) {
      setGovernanceMetrics([]);
      setGovernanceDimensions([]);
      setComplianceByCategory([]);
      setImprovementAreas([]);
      setComplianceTrend([]);
      setChangeRequests([]);
      return;
    }
    projectGovernanceService
      .get(projectId)
      .then((res) => {
        // Reset every slot to its empty/absent value, then overlay whatever the
        // backend returned. Without the reset, a partial response would leave
        // stale data from a previous project (and on a fresh project, the
        // initial empty state below stays put — never silently filled by demo).
        const d = res.governance_data as Record<string, unknown> | null;
        setGovernanceMetrics(Array.isArray(d?.metrics) ? (d!.metrics as GovMetric[]) : []);
        setGovernanceDimensions(Array.isArray(d?.dimensions) ? (d!.dimensions as GovDimension[]) : []);
        setComplianceByCategory(Array.isArray(d?.complianceByCategory) ? (d!.complianceByCategory as GovCompliance[]) : []);
        setImprovementAreas(Array.isArray(d?.improvementAreas) ? (d!.improvementAreas as GovImprovement[]) : []);
        setComplianceTrend(Array.isArray(d?.complianceTrend) ? (d!.complianceTrend as GovTrend[]) : []);
      })
      .catch(() => {
        toast.error('Failed to load governance data');
        setGovernanceMetrics([]);
        setGovernanceDimensions([]);
        setComplianceByCategory([]);
        setImprovementAreas([]);
        setComplianceTrend([]);
      });
    loadChangeRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleCreateChangeRequest = async (data: {
    description: string;
    category: string;
    reason: string;
  }) => {
    if (!projectId) {
      toast.error('Save the project before submitting change requests');
      return;
    }
    setSavingChangeRequest(true);
    try {
      await projectScopeService.createChangeRequest(projectId, {
        description: data.description,
        category: data.category,
        reason: data.reason || undefined,
        // Status defaults to "Pending Review" server-side; submitted_date
        // is set here so the row shows up under today's date immediately.
        submitted_date: new Date().toISOString().slice(0, 10),
      });
      toast.success('Change request submitted');
      setShowChangeRequestForm(false);
      loadChangeRequests();
    } catch (errRaw) {
      const err = errRaw as ApiErrorLike
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to submit change request');
    } finally {
      setSavingChangeRequest(false);
    }
  };

  const overallHealth = governanceDimensions.length
    ? Math.round(governanceDimensions.reduce((sum, d) => sum + d.score, 0) / governanceDimensions.length)
    : 0;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#486581] via-[#5c748e] to-[#486581] rounded-xl p-8 text-white border-b-4 border-[#DAA520]">
        <div className="mb-6">
          <h1 className="text-3xl mb-2">Project Governance</h1>
          <p className="text-[#d4dce5]">Framework compliance, audit tracking, and continuous improvement</p>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-4 gap-6">
          {governanceMetrics.map((metric, idx) => (
            <div key={idx} className="bg-white rounded-lg p-5">
              <div className="flex items-center justify-between mb-3">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  metric.status === 'good' ? 'bg-green-50' :
                  metric.status === 'warning' ? 'bg-orange-50' :
                  'bg-blue-50'
                }`}>
                  {metric.status === 'good' ? (
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  ) : metric.status === 'warning' ? (
                    <AlertCircle className="w-6 h-6 text-orange-600" />
                  ) : (
                    <BarChart3 className="w-6 h-6 text-blue-600" />
                  )}
                </div>
              </div>
              <div className="text-sm text-gray-600 mb-2">{metric.label}</div>
              <div className="text-3xl font-medium text-neutral-950 mb-1">{metric.value}</div>
              <div className={`text-sm ${
                metric.status === 'good' ? 'text-green-600' :
                metric.status === 'warning' ? 'text-orange-600' :
                'text-gray-600'
              }`}>
                {metric.trend}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sub-Navigation Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveSubTab('overview')}
            className={`px-6 py-3 text-sm transition-colors ${
              activeSubTab === 'overview'
                ? 'border-b-2 border-blue-600 text-blue-600 font-medium'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              <span>Dimensions Overview</span>
            </div>
          </button>
          <button
            onClick={() => setActiveSubTab('compliance')}
            className={`px-6 py-3 text-sm transition-colors ${
              activeSubTab === 'compliance'
                ? 'border-b-2 border-blue-600 text-blue-600 font-medium'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span>Compliance Details</span>
            </div>
          </button>
          <button
            onClick={() => setActiveSubTab('improvements')}
            className={`px-6 py-3 text-sm transition-colors ${
              activeSubTab === 'improvements'
                ? 'border-b-2 border-blue-600 text-blue-600 font-medium'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              <span>Improvement Actions</span>
            </div>
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Overview Tab - Radar Chart */}
          {activeSubTab === 'overview' && (
            <div className="space-y-6">
              {/* Radar Chart Section */}
              <div className="grid grid-cols-2 gap-8">
                {/* Radar Chart */}
                <div className="flex items-center justify-center bg-gray-50 rounded-lg p-6">
                  <RadarChart dimensions={governanceDimensions} />
                </div>

                {/* Dimension Details */}
                <div className="space-y-4">
                  <div className="mb-4">
                    <h3 className="text-lg font-medium text-neutral-950 mb-1">Dimension Scores</h3>
                    <p className="text-sm text-gray-600">Performance breakdown across governance areas</p>
                  </div>
                  
                  {governanceDimensions.map((dim, idx) => (
                    <div key={idx} className="py-2">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-900">{dim.dimension}</span>
                        <span className="text-sm font-medium text-gray-700">{dim.score}%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all ${
                              dim.score >= 90 ? 'bg-green-500' :
                              dim.score >= 75 ? 'bg-blue-500' :
                              dim.score >= 60 ? 'bg-yellow-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${dim.score}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-blue-700 font-medium mb-1">Overall Governance Health</div>
                        <div className="text-xs text-gray-600">Aggregate across all dimensions</div>
                      </div>
                      <div className="text-3xl font-medium text-blue-700">{overallHealth}%</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Compliance Trend Chart */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center gap-2 mb-6">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-medium text-neutral-950">Compliance Trend (Last 5 Months)</h3>
                </div>

                <div className="flex items-end justify-between h-48 gap-6 px-4">
                  {complianceTrend.map((item, idx) => (
                    <div key={idx} className="flex-1 flex flex-col items-center">
                      <div className="w-full flex flex-col justify-end h-36 mb-3">
                        <div 
                          className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600 cursor-pointer"
                          style={{ height: `${(item.score / 100) * 100}%` }}
                        ></div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm font-medium text-neutral-950 mb-1">{item.score}%</div>
                        <div className="text-xs text-gray-600">{item.month}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Compliance Tab */}
          {activeSubTab === 'compliance' && (
            <div className="space-y-4">
              <div className="mb-4">
                <h3 className="text-lg font-medium text-neutral-950 mb-1">Governance Framework Compliance</h3>
                <p className="text-sm text-gray-600">Detailed compliance metrics by category</p>
              </div>

              {complianceByCategory.map((item, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-base font-medium text-neutral-950">{item.category}</h4>
                        <span className={`px-2.5 py-1 rounded text-xs font-medium ${
                          item.status === 'excellent' ? 'bg-green-50 text-green-700 border border-green-200' :
                          item.status === 'on-track' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                          'bg-orange-50 text-orange-700 border border-orange-200'
                        }`}>
                          {item.status === 'excellent' ? 'Excellent' :
                           item.status === 'on-track' ? 'On Track' :
                           'Needs Improvement'}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span>Current: <strong className="text-neutral-950">{item.score}%</strong></span>
                        <span>Target: <strong className="text-neutral-950">{item.target}%</strong></span>
                        <span className={item.gap >= 0 ? 'text-green-600' : 'text-red-600'}>
                          Gap: <strong>{item.gap > 0 ? '+' : ''}{item.gap}%</strong>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2.5 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all ${
                          item.status === 'excellent' ? 'bg-green-500' :
                          item.status === 'on-track' ? 'bg-blue-500' :
                          'bg-orange-500'
                        }`}
                        style={{ width: `${item.score}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-700 min-w-[50px] text-right">{item.score}%</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Improvements Tab */}
          {activeSubTab === 'improvements' && (
            <div className="space-y-5">
              <div className="mb-4">
                <h3 className="text-lg font-medium text-neutral-950 mb-1">Key Improvement Areas</h3>
                <p className="text-sm text-gray-600">Identified gaps and recommended actions</p>
              </div>

              {improvementAreas.map((area, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-base font-medium text-neutral-950 mb-2">{area.area}</h4>
                      <div className="flex items-center gap-3">
                        <span className={`px-2.5 py-1 rounded text-xs font-medium ${
                          area.priority === 'High' ? 'bg-red-50 text-red-700 border border-red-200' :
                          'bg-orange-50 text-orange-700 border border-orange-200'
                        }`}>
                          {area.priority} Priority
                        </span>
                        <span className="text-sm text-gray-600">
                          Current: <strong className="text-neutral-950">{area.currentScore}%</strong>
                        </span>
                        <span className="text-sm text-gray-600">
                          Target: <strong className="text-neutral-950">{area.targetScore}%</strong>
                        </span>
                        <span className="text-sm text-red-600">
                          Gap: <strong>-{area.targetScore - area.currentScore}%</strong>
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-5">
                    <div className="bg-red-50 rounded-lg p-4 border border-red-100">
                      <h5 className="text-sm font-medium text-neutral-950 mb-3 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-red-600" />
                        Identified Gaps
                      </h5>
                      <ul className="space-y-2">
                        {area.gaps.map((gap, gIdx) => (
                          <li key={gIdx} className="text-sm text-gray-700 flex items-start gap-2">
                            <span className="text-red-500 mt-0.5">•</span>
                            <span>{gap}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                      <h5 className="text-sm font-medium text-neutral-950 mb-3 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        Recommended Actions
                      </h5>
                      <ul className="space-y-2">
                        {area.actions.map((action, aIdx) => (
                          <li key={aIdx} className="text-sm text-gray-700 flex items-start gap-2">
                            <span className="text-green-600 mt-0.5">✓</span>
                            <span>{action}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Additional Governance Details - From Main Governance Hub */}
      
      {/* Required Documentation Status */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-6">
          <FileText className="w-5 h-5 text-blue-600" />
          <h2 className="text-xl text-neutral-950">Required Documentation Status</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-neutral-950">Document Name</th>
                <th className="text-left py-3 px-4 text-neutral-950">Status</th>
                <th className="text-left py-3 px-4 text-neutral-950">Last Updated</th>
                <th className="text-left py-3 px-4 text-neutral-950">Owner</th>
                <th className="text-left py-3 px-4 text-neutral-950">Actions</th>
              </tr>
            </thead>
            <tbody>
              {[
                { name: 'Project Charter', status: 'Complete', lastUpdated: '2024-10-15', owner: 'Sarah Wilson' },
                { name: 'Risk Register', status: 'Complete', lastUpdated: '2024-11-28', owner: 'Sarah Wilson' },
                { name: 'Stakeholder Register', status: 'Complete', lastUpdated: '2024-10-20', owner: 'Sarah Wilson' },
                { name: 'Communication Plan', status: 'Missing', lastUpdated: '-', owner: 'Sarah Wilson' },
                { name: 'Quality Management Plan', status: 'In Progress', lastUpdated: '2024-11-25', owner: 'Sarah Wilson' },
                { name: 'Change Management Plan', status: 'Missing', lastUpdated: '-', owner: 'Sarah Wilson' },
              ].map((doc, idx) => (
                <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-4 text-neutral-950">{doc.name}</td>
                  <td className="py-4 px-4">
                    <span className={`px-3 py-1 rounded ${
                      doc.status === 'Complete' ? 'bg-green-50 text-green-600' :
                      doc.status === 'In Progress' ? 'bg-blue-50 text-blue-600' :
                      'bg-red-50 text-red-600'
                    }`}>
                      {doc.status}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-gray-600">{doc.lastUpdated}</td>
                  <td className="py-4 px-4 text-gray-600">{doc.owner}</td>
                  <td className="py-4 px-4">
                    {doc.status === 'Complete' ? (
                      <button className="text-blue-600 hover:text-blue-700 flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        View
                      </button>
                    ) : (
                      <button className="text-orange-600 hover:text-orange-700 flex items-center gap-1">
                        <Download className="w-4 h-4" />
                        Upload
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Change Requests */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl text-neutral-950">Active Change Requests</h2>
          </div>
          <button
            type="button"
            onClick={() => setShowChangeRequestForm(true)}
            disabled={!projectId}
            title={!projectId ? 'Save the project first' : undefined}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Submit Change Request
          </button>
        </div>

        {changeRequests.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-500">
            No change requests yet. Click "Submit Change Request" above to log one.
          </div>
        ) : (
          <div className="space-y-3">
            {changeRequests.map((cr) => (
              <div key={cr.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-neutral-950 mb-1">{cr.description}</h3>
                    <p className="text-sm text-gray-600">CR-{String(cr.id).padStart(4, '0')}</p>
                  </div>
                  <span className={`px-3 py-1 rounded text-sm ${
                    cr.status === 'Approved'
                      ? 'bg-green-50 text-green-600'
                      : cr.status === 'Rejected'
                        ? 'bg-red-50 text-red-600'
                        : 'bg-yellow-50 text-yellow-700'
                  }`}>
                    {cr.status}
                  </span>
                </div>
                <div className="flex items-center gap-6 text-sm text-gray-600 flex-wrap">
                  {cr.approver && <div>Approver: {cr.approver}</div>}
                  {cr.submitted_date && <div>Submitted: {cr.submitted_date}</div>}
                  <div>
                    <span className="px-2 py-1 rounded bg-blue-50 text-blue-700">
                      {cr.category}
                    </span>
                  </div>
                </div>
                {cr.reason && (
                  <p className="text-sm text-gray-700 mt-3 italic">"{cr.reason}"</p>
                )}
              </div>
            ))}
          </div>
        )}

        {showChangeRequestForm && (
          <ChangeRequestForm
            onClose={() => setShowChangeRequestForm(false)}
            onSave={handleCreateChangeRequest}
            saving={savingChangeRequest}
          />
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Sprint 20 — Submit Change Request modal
//
// Mirrors the inline modal pattern used by RisksSection / DecisionsSection.
// Keeps the field set minimal (description / category / reason) — the
// backend schema supports more (approver, impact_analysis, status) but
// those are governance-board concerns set later in the workflow, not at
// request-submission time.
// ──────────────────────────────────────────────────────────────────────

function ChangeRequestForm({
  onClose,
  onSave,
  saving,
}: {
  onClose: () => void;
  onSave: (data: { description: string; category: string; reason: string }) => void;
  saving: boolean;
}) {
  const [form, setForm] = useState({
    description: '',
    category: 'Scope',
    reason: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.description.trim()) {
      toast.error('Description is required');
      return;
    }
    onSave(form);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-xl">Submit Change Request</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className="block text-sm mb-2">Description *</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              required
              autoFocus
              placeholder="What change are you requesting?"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm mb-2">Category *</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {CR_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm mb-2">Reason / Justification</label>
            <textarea
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              rows={3}
              placeholder="Why is this change needed?"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !form.description.trim()}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}